import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";
import Loading from "../../../../components/Loading/Loading";
import { useToggle } from "../../../../hooks/useToggle";
import localeText from "../../../../locale";
import ExtracoinService from "../../../../service/ExtracoinService";
import GetInfoService from "../../../../service/GetInfoService";
import handleLocale from "../../../../utilities/LocaleSwitch";
import { formatPrice } from "../../../../utilities/formatPrice";
import { locale } from "../../../../utilities/locale/locale";
import CreateFinancialPasswordModal from "../../../../components/CreateFinancialPasswordModal/CreateFinancialPasswordModal";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import useFinantialPassword from "../../../../hooks/useFinantialPassword";

const ExtracoinTransfer = (props) => {
  const { currency, extracoinInfo } = props;

  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState(0);
  const [toUsername, setToUsername] = useState("");
  const [toUsernameIcon, setToUsernameIcon] = useState("");
  const [loadingTransaction, setLoadingTransaction] = useState(false);
  const [transactionInfo, setTransactionInfo] = useState({});
  const { setAsFalse, setAsTrue, value } = useToggle();

  const toUsernameRef = useRef(null);
  const toast = useRef(null);

  const financialPasswordRef = useRef(null);
  const [financialPassword, setFinancialPassword] = useState("");

  const { showErrorToast: newShowErrorToast, showSuccessToast } = useToastContext();
  const {
    setAsFalse: closeFinancialPasswordModal,
    setAsTrue: openFinancialPasswordModal,
    value: isVisibleModal,
  } = useToggle();
  const {
    isLoading,
    isLoadingCreateFinancialPassword,
    hasFinantialPassword,
    createFinantialPassword,
    checkFinantialPassword,
  } = useFinantialPassword({
    onError: ({ code }) => newShowErrorToast({ code }),
    onSuccess: () => showSuccessToast({ detail: "passwordCreatedSuccessfully" }),
  });

  let currencyCode = "USD";
  let localeISO = "en-US";

  currencyCode = currency !== "$" ? currency : "USD";

  localeISO = handleLocale(locale);

  const getInfoService = new GetInfoService();
  const extracoinService = new ExtracoinService();

  async function findUsername(username) {
    if (username.length === 0) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("usernameCantBeEmpty"),
        life: 6000,
      });
      toUsernameRef.current.style.border = "1px solid red";
      setToUsernameIcon("pi pi-times");
      return false;
    }

    try {
      if (username.length > 0) toUsernameRef.current.style.border = "1px solid #ced4da";

      setToUsernameIcon("pi pi-spin pi-spinner");
      const res = await getInfoService.getFindUsername(username);

      if (res.messageCode !== 200001) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("usernameNotFound"),
          life: 6000,
        });

        setToUsernameIcon("pi pi-times");
        toUsernameRef.current.style.border = "1px solid red";

        return false;
      } else {
        setToUsername(username);
        setToUsernameIcon("pi pi-check");
        toUsernameRef.current.style.border = "1px solid #46a748";
        return true;
      }
    } catch (e) {
      console.log(e);
    }
  }

  async function createIntent(token, amount, fromUsername, toUsername) {
    try {
      setLoading(true);

      if (amount === 0) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("amountCantBeZero"),
          life: 6000,
        });
        setLoading(false);
        return false;
      }

      const res = await extracoinService.postTransferIntent(
        token,
        amount,
        fromUsername,
        toUsername
      );

      if (res.messageCode !== 200001) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("data").find((msg) => msg.code === res.messageCode).message,
          life: 6000,
        });
        setLoading(false);
      } else {
        setTransactionInfo(res.result);
        setAsTrue();
        setLoading(false);
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }
      setLoading(false);
      console.log(e);
    }
  }

  async function acceptTransaction(token, username, transactionId) {
    try {
      if (financialPassword.length === 0) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("financialPasswordCantBeEmpty"),
          life: 6000,
        });
        financialPasswordRef.current.style.border = "1px solid red";
        return false;
      }
      setLoadingTransaction(true);
      const res = await extracoinService.postFromAgree(
        token,
        username,
        transactionId,
        financialPassword
      );

      if (res.messageCode === 500166) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("financialPasswordIsWrong"),
          life: 6000,
        });
        setLoadingTransaction(false);
      } else if (res.messageCode !== 200000) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("data").find((msg) => msg.code === res.messageCode).message,
          life: 6000,
        });
        setLoadingTransaction(false);
      } else {
        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: localeText("transactionConfirmed"),
          life: 6000,
        });
        setLoadingTransaction(false);
        setAsFalse();
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }
      setLoadingTransaction(false);
      console.log(e);
    }
  }
  const canCreateFinancialPassword =
    /seller/i.test(props?.userTypeInfo) ||
    /manager/i.test(props?.userTypeInfo) ||
    /partner/i.test(props?.userTypeInfo) ||
    /promoter/i.test(props?.userTypeInfo);

  return (
    <>
      <CreateFinancialPasswordModal
        isVisible={isVisibleModal}
        setModalNotVisible={closeFinancialPasswordModal}
        setModalVisible={openFinancialPasswordModal}
        createFinantialPassword={createFinantialPassword}
        canCreateFinancialPassword={canCreateFinancialPassword}
        isLoadingCreateFinancialPassword={isLoadingCreateFinancialPassword}
        isFirstPassword={!hasFinantialPassword}
      />

      <div className="card border-1 border-300">
        <Toast ref={toast} position="top-left" />
        <Dialog
          header={
            <>
              <i className="pi pi-exclamation-triangle text-2xl pr-2"></i>
              <span className="font-bold m-0 p-0 text-2xl">
                {localeText("attention").toUpperCase()}:
              </span>{" "}
              {localeText("confirmYourAction")}
            </>
          }
          visible={value}
          className="w-30rem mx-2"
          onHide={() => {
            if (!loadingTransaction) setAsFalse();
          }}
          draggable={false}
        >
          <>
            {loading ? (
              <div>
                <i className="pi pi-spin pi-spinner text-6xl text-400 my-6"></i>
              </div>
            ) : (
              <div className="mr-3 ml-3">
                {transactionInfo.taxFrom && (
                  <p>
                    {localeText("confirmTransactionExplanation")}{" "}
                    <b className="text-xl">
                      {currency} {formatPrice(transactionInfo.taxFrom)}.
                    </b>
                  </p>
                )}

                <p>{localeText("askFinancialPassword")}</p>
                <InputText
                  ref={financialPasswordRef}
                  name="financialPassword"
                  type="text"
                  value={financialPassword}
                  onChange={(e) => setFinancialPassword(e.target.value)}
                  onFocus={() => (financialPasswordRef.current.style.border = "1px solid #ced4da")}
                />
                <p className="mt-2">{localeText("doYouAgree")}</p>
                <div className="flex gap-2 justify-content-end">
                  <Button onClick={setAsFalse} severity="danger" disabled={loadingTransaction}>
                    {localeText("no")}
                  </Button>
                  <Button
                    onClick={() =>
                      acceptTransaction(
                        localStorage.getItem("__extraconomy_token"),
                        localStorage.getItem("__extraconomy_username"),
                        transactionInfo.id
                      )
                    }
                    disabled={loadingTransaction}
                    className={`${loadingTransaction ? "opacity-80" : ""}`}
                  >
                    <div className="flex justify-content-center align-items-center gap-2">
                      {loadingTransaction && <Loading classNameIcon={"text-sm"} disableMargin />}
                      <p>{localeText("yes")}</p>
                    </div>
                  </Button>
                </div>
              </div>
            )}
          </>
        </Dialog>

        {extracoinInfo && (
          <>
            <h3 className="my-4">{localeText("extracoinTransfer")}</h3>
            <hr />

            <ul className="list-none p-0 mt-6">
              <li className="font-bold">
                <label htmlFor="extracoin-amount">{localeText("amount")}:</label>
              </li>
              <li className="mt-2">
                <InputNumber
                  name="extracoin-amount"
                  value={amount}
                  onValueChange={(e) => setAmount(e.value)}
                  min={0}
                  max={extracoinInfo.balance}
                  mode="currency"
                  showButtons
                  buttonLayout="horizontal"
                  decrementButtonClassName="p-button-danger"
                  incrementButtonClassName="p-button-success"
                  incrementButtonIcon="pi pi-plus"
                  decrementButtonIcon="pi pi-minus"
                  currency={currencyCode}
                  locale={localeISO}
                  step={0.1}
                />
              </li>

              <li className="font-bold mt-4">
                <label htmlFor="username">{localeText("receivingUsername")}:</label>
              </li>
              <li className="mt-2">
                <span className="p-input-icon-right">
                  <i className={toUsernameIcon} />
                  <InputText
                    ref={toUsernameRef}
                    name="username"
                    type="text"
                    value={toUsername}
                    onChange={(e) => setToUsername(e.target.value)}
                    onBlur={(e) => findUsername(e.target.value)}
                    onFocus={() => {
                      setToUsernameIcon("");
                      toUsernameRef.current.style.border = "1px solid #ced4da";
                    }}
                  />
                </span>
              </li>
            </ul>
            <div className="flex gap-3 flex-wrap align-items-center mt-5">
              <Button
                label={localeText("transfer")}
                onClick={() =>
                  createIntent(
                    localStorage.getItem("__extraconomy_token"),
                    amount,
                    localStorage.getItem("__extraconomy_username"),
                    toUsername
                  )
                }
                loading={loading}
              />
              {canCreateFinancialPassword && (
                <Button
                  label={localeText("createNewFinancialPassword")}
                  onClick={() => {
                    checkFinantialPassword().finally(() => {
                      openFinancialPasswordModal();
                    });
                  }}
                  loading={isLoading}
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ExtracoinTransfer;
